import { createSelector } from 'reselect'
import { isAccommodationItem, isBedbankItem, isBNBLLEHotelItem, isBookingProtectionItem, isLuxPlusSubscriptionItem, isInsuranceItem, isFlightItem, isLEHotelItem, isVillaItem } from 'lib/checkout/checkoutUtils'
import { groupBy, isNonEmptyArray, min, max, nonNullable, sum, unique, arrayToObject, last } from 'lib/array/arrayUtils'
import { filterCollinsonLounges, collinsonLoungeSupportsFlightType, getExperienceBookingViewCartData, getExperienceListKey, mapExperienceToBookingView, getTargetAudience, getExperienceBookingViewCartDataFromTransferItems, getComplimentaryTransferParams, buildOfferTagsCacheKey } from 'lib/experiences/experienceUtils'
import { computeTopOfferListFiltersFromParams } from 'lib/experiences/computeTopOfferListFilters'
import config from 'constants/config'
import { EmptyExperienceList, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXPERIENCE_TRANSFER_ID, ExperienceBookingType } from 'constants/experience'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { checkoutAccommodationOfferView, getAccommodationEndDate, getAccommodationItems, getAccommodationStartDate, getAccommodationOccupantsFromCart } from './view/accommodation'
import { getFullOffers } from 'selectors/offerSelectors'
import { getVillaItems, getVillaViews } from 'checkout/selectors/view/villa'
import { getMerchantFeeByOrderItem } from 'lib/payment/paymentUtils'
import { isExperienceItemUpcoming, isTransferItemUpcoming } from 'lib/order/isUpcoming'
import { isEmptyObject } from 'lib/object/objectUtils'
import { hasFijiHotelInCart } from './hasFijiHotelInCart'
import type { ExperienceListFilters } from 'actions/ExperienceActions'
import getExperienceItems from './view/items/getExperienceItems'
import getTransferItems from './view/items/getTransferItems'
import { getBedbankItems, getInstantBookingLEHotelItems, getLeHotelItems } from './view/hotels'
import getTourV2ItemViews from './view/itemViews/getTourV2ItemViews'
import getHotelItemViews from './view/itemViews/getHotelItemViews'
import getBedbankItemViews from './view/itemViews/getBedbankItemViews'
import { getFlightItemsView, getIsInternationalFlight } from './view/flights'
import { getClosestAirport } from 'selectors/flightsSelectors'
import findClosestAirport from 'lib/flights/findClosestAirport'
import { getValidTransferOptions, getDistanceBetweenPropertyAndAirport } from 'components/Experiences/ExperienceTransfers/ExperienceTransferUtils'
import getCanRedeemLoungePassLoyaltyBenefit from './loyalty/getCanRedeemLoungePassLoyaltyBenefit'
import getExperienceItemsView from './view/itemViews/getExperienceItemsView'
import { CHECKOUT_ITEM_TYPE_EXPERIENCE } from 'constants/checkout'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { isSpoofed } from 'selectors/featuresSelectors'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'
import { getFormattedExperienceItems } from 'checkout/selectors/payment/orderCreation/getFormattedExperienceItems'
import getFormattedLoyaltyLoungePassItems from 'checkout/selectors/payment/orderCreation/getFormattedLoyaltyLoungePassItems'
import { getFormattedTransferItems } from 'checkout/selectors/payment/orderCreationSelectors'
import buildLoyaltyLoungePassFilters from 'lib/experiences/buildLoyaltyLoungePassFilters'
import getLoyaltyLoungePassItemViews from './view/itemViews/getLoyaltyLoungePassItemViews'
import getTransferItemsView from './view/itemViews/getTransferItemsView'

export const getLeOfferInCart = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (items, leOffers): App.Offer | undefined => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isLEHotelItem(item)) {
      return leOffers?.[item.offerId]
    }
  },
)

export const getVillaOfferInCart = createSelector(
  (state: App.State) => getVillaItems(state),
  (state: App.State) => state.offer.offers,
  (items, villaOffers): App.Offer | undefined => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isVillaItem(item)) {
      return villaOffers?.[item.offerId]
    }
  },
)

export const getBedbankOfferInCart = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getFullOffers(state),
  (items, offers): App.BedbankOffer | undefined => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isBedbankItem(item)) {
      return offers?.[item.offerId] as App.BedbankOffer
    }
  },
)

export const getHotelInCart = createSelector(
  (state: App.State) => getLeOfferInCart(state),
  (state: App.State) => getVillaOfferInCart(state),
  (state: App.State) => getBedbankOfferInCart(state),
  (leOffer, villaOffer, bedbankOffer) => leOffer ?? bedbankOffer ?? villaOffer,
)

export const getExperiencesEnabled = createSelector(
  (state: App.State) => getLeOfferInCart(state),
  (state: App.State) => getVillaOfferInCart(state),
  (state: App.State) => getBedbankOfferInCart(state),
  (offer, villaOffer, bedBankOffer) => {
    if (offer) {
      return config.EXPERIENCES_ENABLED && offer.experiencesInFlow
    }

    if (villaOffer) {
      return config.EXPERIENCES_ENABLED && villaOffer.experiencesInFlow
    }

    if (bedBankOffer) {
      return config.EXPERIENCES_ENABLED && config.EXPERIENCES_BEDBANK_INFLOW_ENABLED
    }

    return config.EXPERIENCES_ENABLED
  },
)

export const getCartOfferGeoCoords = createSelector(
  (state: App.State) => getHotelInCart(state),
  (offer) => {
    if (offer) {
      return {
        latitude: offer.property?.latitude,
        longitude: offer.property?.longitude,
      }
    } else {
      return {}
    }
  },
)

export const getCartOfferLocation = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (items, leOffers, bedbankOffers) => {
    // only the first offer will be considered now
    const item = items[0]
    if (item && isLEHotelItem(item)) {
      const offer = leOffers?.[item.offerId]
      return offer?.location ?? ''
    }
    if (item && isBedbankItem(item)) {
      const offer = bedbankOffers?.[item.offerId]
      const property = offer?.property
      if (property) {
        return [property.address.city, property.address.stateProvinceName].filter(Boolean).join(', ')
      }
    }
    return ''
  },
)

export const getCheckoutExperienceDateRange = createSelector(
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (from, to): App.Checkout.ExperienceDateRange => {
    return {
      from: from ? moment(from).add(-7, 'days').format(ISO_DATE_FORMAT) : undefined,
      to: to ? moment(to).add(7, 'days').format(ISO_DATE_FORMAT) : undefined,
    }
  },
)

export type ExperiencesCheckoutTopOfferListId = 'TRANSFER' |
  'WATER_TRANSFER' |
  'HOTEL_CURATED' |
  'REGIONAL_CURATED' |
  'DISTANCE_SHORT' |
  'DISTANCE_MEDIUM' |
  'DISTANCE_LONG' |
  'FALLBACK' |
  'TAG_ASSIGNED'

export type ExperiencesCheckoutTopOfferListFilters = {
  key: string
  filters: ExperienceListFilters
  id: ExperiencesCheckoutTopOfferListId
}

export const getTopOfferListFilters = createSelector(
  (state: App.State) => getCartOfferGeoCoords(state),
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => getLeOfferInCart(state),
  (state: App.State) => getCheckoutExperienceDateRange(state),
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => isAccommodationWithNoIncludedTransfer(state),
  (state: App.State) => getAccommodationTagIds(state),
  (geoCoords, currencyCode, hotelOffer, availabilityParams, accommodationItems, isAccommodationWithNoIncludedTransfer, tagIds) => {
    if (!geoCoords.latitude || !geoCoords.longitude) return []

    // Build target audience based on hotel booked
    const adults = accommodationItems[0]?.occupancy?.adults
    const children = accommodationItems[0]?.occupancy?.children
    const targetAudience = getTargetAudience(adults ?? 1, children)

    const raw = computeTopOfferListFiltersFromParams({
      currencyCode,
      geoCoords,
      hotelOffer: hotelOffer ? { id: hotelOffer.id, experiencesCurated: hotelOffer.experiencesCurated } : undefined,
      availability: availabilityParams,
      hasNoIncludedTransfer: isAccommodationWithNoIncludedTransfer,
      targetAudience,
      tagIds,
    })

    return raw.map(({ id, filters }) => ({ key: getExperienceListKey(filters), filters: filters, id: id }))
  },
)

export const getExperienceOfferList = createSelector(
  (state: App.State) => state.experience.experienceLists,
  (state: App.State) => getHotelInCart(state),
  (state: App.State) => getExperiencesEnabled(state),
  (state: App.State) => getTopOfferListFilters(state),
  (lists, hotelOffer, isExperienceEnabled, filters): App.ApiCallState<{ ids: Array<string> }> => {
    if (!hotelOffer) return { fetching: true, ids: [] }
    if (!isExperienceEnabled) return { fetching: false, ids: [] }

    const newLists = filters.map(filter => (lists[filter.key] ?? EmptyExperienceList))

    return {
      fetching: isExperienceEnabled && newLists.some(list => list.fetching),
      ids: unique(newLists.flatMap(list => list.ids ?? [])),
    }
  },
)

export const getExperiencesViews = createSelector(
  (state: App.State) => state.experience.experiences,
  (state: App.State) => getHotelInCart(state),
  (state: App.State) => getBedbankOfferInCart(state),
  (experiences, offer, bedbankOffer): Array<App.ExperienceBookingView> => {
    return Object.values(experiences).filter(Boolean).map(exp =>
      mapExperienceToBookingView(exp, undefined, offer?.property ?? bedbankOffer?.property),
    )
  },
)

export const getAllPurchasableExperiences = createSelector(
  (state: App.State) => getExperiencesViews(state),
  (state: App.State) => getExperienceItems(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (experienceViews, cartExperienceItems, cartTransferItems, accommodationStartDate, accommodationEndDate): Array<App.ExperienceBookingView> => {
    const minDate = accommodationEndDate ?? accommodationStartDate ?? Date.now()

    return experienceViews
      .filter(item => {
        // Hide Collinson provider offers from checkout inflow
        if (item.originalExperience.provider === 'cln') return false

        // Hide expired offers
        if (item.originalExperience.expirationDate && moment(item.originalExperience.expirationDate).isBefore(moment(minDate))) return false

        return true
      })
      .map(item => {
        const experienceItem = cartExperienceItems?.find(i => i.experienceId === item.id)
        const transferItems = cartTransferItems?.filter(i => i.experienceId === item.id)
        let cartData: App.ExperienceBookingView['cartData']

        if (experienceItem) {
          cartData = getExperienceBookingViewCartData(experienceItem)
        } else if (transferItems.length) {
          cartData = getExperienceBookingViewCartDataFromTransferItems(transferItems)
        }

        return {
          ...item,
          added: !!cartData?.count,
          cartData,
        }
      })
  },
)

export const getTopOffersInflow = createSelector(
  (state: App.State) => getExperienceOfferList(state),
  (state: App.State) => getAllPurchasableExperiences(state),
  (topOfferList, allExperiences) => {
    // Keep the order of items from topOfferList
    const orderedExperiences = nonNullable(topOfferList.ids.map(id => allExperiences.find(exp => exp.id === id)))
    return orderedExperiences
  },
)

export const getTopOffersInFlowWithoutTransfer = createSelector(
  (state: App.State) => getTopOffersInflow(state),
  (experiences) => experiences.filter(experience => {
    const withNoCategory = !experience.categories.length
    const withNoTransferCategory = !experience.categories.some(category => category.id === EXPERIENCE_TRANSFER_ID)
    const isNotSetTransfer = !experience.features?.transfer

    return withNoCategory || (withNoTransferCategory && isNotSetTransfer)
  }),
)

export const getExperienceRefundDetails = createSelector(
  (state: App.State) => state.experience.refundDetails,
  (state: App.State) => state.experience.cancellingExperiences,
  (state: App.State, order: App.Order) => order,
  (experiencesRefundDetails, cancellingExperiences, order) => {
    let total = 0
    const refundableItems: Array<string> = []
    let itemToCancel = { amount: 0, fee: 0 }

    const refundDetails = Object.values(experiencesRefundDetails)

    if (refundDetails?.length) {
      total = sum(refundDetails.map(refund => {
        const merchantFeeItem = getMerchantFeeByOrderItem(order.merchantFees, refund.orderItemId)
        const refundableMerchantFee = merchantFeeItem?.shouldAutoRefund ? merchantFeeItem?.amount : 0
        return refund.refundAmount + refundableMerchantFee
      }))

      refundDetails.forEach(refund => {
        // Do not allow refund it no amount available among the items
        if (refund.refundAmount > 0) refundableItems.push(refund.orderItemId)
      })
    }

    if (refundDetails?.length && cancellingExperiences?.length) {
      const items = refundDetails.filter(refund => cancellingExperiences.some(item => item.id === refund.orderItemId))
      itemToCancel = {
        amount: sum(items.map(refund => {
          const merchantFeeItem = getMerchantFeeByOrderItem(order.merchantFees, refund.orderItemId)
          const refundableMerchantFee = merchantFeeItem?.shouldAutoRefund ? merchantFeeItem?.amount : 0
          return refund.refundAmount + refundableMerchantFee
        })),
        fee: sum(items.map(refund => {
          const merchantFeeItem = getMerchantFeeByOrderItem(order.merchantFees, refund.orderItemId)
          const nonRefundableMerchantFee = merchantFeeItem && !merchantFeeItem.shouldAutoRefund ? merchantFeeItem?.amount : 0
          return refund.refundFee + nonRefundableMerchantFee
        })),

      }
    }

    return {
      total,
      itemToCancel,
      refundableItems,
    }
  },
)

export const getChangeDatesExperiencesOutsideHotelStay = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (order, startDate, endDate): Array<App.OrderExperienceItem> => {
    if (!order) return []
    const experiences = order.experienceItems.filter((e) => isExperienceItemUpcoming(e, order))

    return experiences.filter(item => {
      if (startDate && endDate && moment(item.date).isBetween(startDate, endDate, null, '[]')) {
        return false
      }

      if (item.complimentary) {
        return false
      }

      return true
    })
  },
)

export const getChangeDatesTransfersOutsideHotelStay = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (order, startDate, endDate): Array<App.OrderTransferItem> => {
    if (!order) return []

    const transfers = order.transferItems.filter(isTransferItemUpcoming)

    return transfers.filter(item => {
      if (startDate && endDate && moment(item.date).isBetween(startDate, endDate, null, '[]')) {
        return false
      }

      if (item.complimentary) {
        return false
      }

      return true
    })
  },
)

function isTransferWithinBookingCutoff(bookingCutoffHours: number | undefined, checkInDate: string | undefined): boolean {
  if (!bookingCutoffHours || !checkInDate) return false
  const minAllowedDate = moment().add(bookingCutoffHours, 'hours')
  return moment(checkInDate).isBefore(minAllowedDate, 'day')
}

export const getOneClickExperienceTransfer = createSelector(
  (state: App.State) => getAllPurchasableExperiences(state),
  (state: App.State) => getTransferItems(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => getAccommodationStartDate(state),
  (purchasableExperiences, checkoutTransferItems, allExperiences, startDate): App.ExperienceBookingView | undefined => {
    const MAX_KM_FROM_HOTEL = 100

    // If already there's a transfer on cart
    if (
      isNonEmptyArray(checkoutTransferItems) &&
      !isEmptyObject(allExperiences) &&
      allExperiences[checkoutTransferItems[0].experienceId]
    ) {
      return mapExperienceToBookingView(allExperiences[checkoutTransferItems[0].experienceId])
    }

    const transfers = Object.values(purchasableExperiences)?.filter(experience =>
      experience.categories.some(category => category.id === EXPERIENCE_TRANSFER_ID) &&
      !!experience.features?.transfer &&
      !!experience.distance &&
      experience.distance <= MAX_KM_FROM_HOTEL &&
      experience.price > 0 &&
      !isTransferWithinBookingCutoff(experience.originalExperience.bookingCutoffHours, startDate),
    )
    if (!transfers.length) return

    const preferredTransfers = transfers.filter(transfer => !!transfer.features?.transfer?.preferredTransfer)
    if (preferredTransfers.length) {
      return min(preferredTransfers, (transfer) => transfer.price)
    }

    return min(transfers, (transfer) => transfer.price)
  },
)

export const isAccommodationWithNoIncludedTransfer = createSelector(
  (state: App.State) => getLeHotelItems(state),
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => state.experience.experienceLists,
  (leHotelItems, bedbankItems, experienceLists) => {
    if (!leHotelItems.length && !bedbankItems.length) return false

    const accommodationContainsComplementaryTransfer = [...leHotelItems, ...bedbankItems].some(item => {
      const { packageId } = item
      if (!packageId) return false

      const params = getComplimentaryTransferParams(packageId)
      const key = getExperienceListKey(params)
      const complementaryExperience = experienceLists[key]

      return !complementaryExperience?.fetching && !!complementaryExperience?.total
    })

    return !accommodationContainsComplementaryTransfer
  },
)

export const isSingleAccommodationCheckout = createSelector(
  (state: App.State) => getInstantBookingLEHotelItems(state),
  (state: App.State) => getBedbankItems(state),
  (leHotelItems, bedbankItems) => {
    const accommodationItems = [...leHotelItems, ...bedbankItems]

    // Validate is single hotel or multi-room
    const uniqueHotelIds = unique(
      accommodationItems.map(item => item.offerId).filter(Boolean),
    )
    if (uniqueHotelIds.length > 1) return false

    // When is multi-room, dates should be the same
    const uniqueDateRanges = unique(
      accommodationItems
        .map(item => `${item.checkIn}-${item.checkOut}`,
        )
        .filter(Boolean),
    )
    if (uniqueDateRanges.length > 1) return false

    return true
  },
)

/**
 * One click transfer is enabled when:
 * - There is a transfer available for the accommodation location
 * - The user has an accommodation in the cart
 * - The accommodation doesn't have a transfer included in the offer
 * - The accommodation is not located in Fiji due to logistical complications
 *   In this region, transfers require both a vehicle and a water ferry, making the process less straightforward
 * - Multiple accommodation items are allowed only when they refer to
 *   the same hotel (multi-room scenario)
 */
export const isOneClickExperienceTransferEnabled = createSelector(
  (state: App.State) => getOneClickExperienceTransfer(state),
  (state: App.State) => isAccommodationWithNoIncludedTransfer(state),
  (state: App.State) => hasFijiHotelInCart(state),
  (state: App.State) => getExperiencesEnabled(state),
  (state: App.State) => isSingleAccommodationCheckout(state),
  (state: App.State) => getAccommodationItems(state),
  (
    oneClickTransfer,
    isAccommodationWithNoIncludedTransfer,
    hasFijiHotelInCart,
    isExperienceEnabled,
    isSingleAccommodation,
    accommodationItems,
  ) => {
    const hasBNBLItem = accommodationItems.some(isBNBLLEHotelItem)

    return (
      !!oneClickTransfer &&
      isAccommodationWithNoIncludedTransfer &&
      !hasFijiHotelInCart &&
      isExperienceEnabled &&
      isSingleAccommodation &&
      !hasBNBLItem
    )
  },
)

export const getExperienceOneClickTransferOptions = createSelector(
  (state: App.State) => state.experience.experienceTransferOptions,
  (state: App.State) => getOneClickExperienceTransfer(state),
  (transferOptions, oneClickTransferOffer) => {
    const options = Object.values(transferOptions).flatMap(option => option?.transfers ?? []).filter(item => item.experienceId === oneClickTransferOffer?.id)

    const [toHotelOptions, toAirportOptions] = [
      options.filter(transfer => transfer?.transferTypes.includes('AIRPORT-TO-HOTEL')),
      options.filter(transfer => transfer?.transferTypes.includes('HOTEL-TO-AIRPORT')),
    ]

    return {
      toHotelOptions,
      toAirportOptions,
    }
  },
)

export const getDefaultTransfers = createSelector(
  (state: App.State) => getTransferItems(state),
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (state: App.State) => getAccommodationOccupantsFromCart(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State, experience?: App.ExperienceBookingView) => experience?.originalExperience,
  (existingTransfers, startDate, endDate, totalOccupants, flightItems, offerView, experience): {
    toHotel: App.ExperienceTransferView
    toAirport: App.ExperienceTransferView
  } => {
    const existingToHotel = existingTransfers.find(transfer => transfer.experienceId === experience?.id && transfer.transfer.type === 'AIRPORT-TO-HOTEL')?.transfer
    const existingToAirport = existingTransfers.find(transfer => transfer.experienceId === experience?.id && transfer.transfer.type === 'HOTEL-TO-AIRPORT')?.transfer

    const departingFlight = last(flightItems.data[0]?.flights[0]?.journeyFlight.flights ?? [])
    const returningFlight = flightItems.data[0]?.flights[1]?.journeyFlight.flights[0]
    const property = (offerView.data[0]?.offer as any)?.property

    return {
      toHotel: existingToHotel ?? {
        type: 'AIRPORT-TO-HOTEL',
        date: departingFlight ? departingFlight.arrivalDate : startDate,
        time: departingFlight ? departingFlight.arrivalTime : undefined,
        travellers: {
          adults: totalOccupants?.adults || 0,
          children: totalOccupants?.children || 0,
        },
        hotel: {
          latitude: property?.latitude,
          longitude: property?.longitude,
          kmFromAirport: getDistanceBetweenPropertyAndAirport(property, experience?.airport),
          name: property?.name,
        },
        flightNumber: departingFlight ? `${departingFlight.carrier}${departingFlight.flightNumber}` : undefined,
        baggage: {
          bags: 0,
          oversizedBags: 0,
        },
      },
      toAirport: existingToAirport ?? {
        type: 'HOTEL-TO-AIRPORT',
        date: returningFlight ? returningFlight.departingDate : endDate,
        time: returningFlight ? returningFlight.departingTime : undefined,
        travellers: {
          adults: totalOccupants?.adults || 0,
          children: totalOccupants?.children || 0,
        },
        flightNumber: returningFlight ? `${returningFlight.carrier}${returningFlight.flightNumber}` : undefined,
        hotel: {
          latitude: property?.latitude,
          longitude: property?.longitude,
          kmFromAirport: getDistanceBetweenPropertyAndAirport(property, experience?.airport),
          name: property?.name,
        },
        baggage: {
          bags: 0,
          oversizedBags: 0,
        },
      },
    }
  },
)

export const getIsOneClickTransferAvailable = createSelector(
  (state: App.State) => getOneClickExperienceTransfer(state),
  (state: App.State) => getExperienceOneClickTransferOptions(state),
  (state: App.State) => {
    const transfer = getOneClickExperienceTransfer(state)
    return transfer ? getDefaultTransfers(state, transfer) : undefined
  },
  (
    oneClickTransfer,
    onClickTransferOptions,
    defaultTransfers,
  ): boolean => {
    if (!oneClickTransfer || !defaultTransfers) return false

    const hasValidToHotel = getValidTransferOptions(defaultTransfers.toHotel, onClickTransferOptions.toHotelOptions).length > 0
    const hasValidToAirport = getValidTransferOptions(defaultTransfers.toAirport, onClickTransferOptions.toAirportOptions).length > 0
    return hasValidToHotel || hasValidToAirport
  },
)

export const getLoyaltyCheckoutLoungePassFilters = createSelector(
  (state: App.State) => getBedbankItemViews(state),
  (state: App.State) => getTourV2ItemViews(state),
  (state: App.State) => getHotelItemViews(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getClosestAirport(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.auth.account.recentlyUsedAirportCode,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.geo.airports,
  (state: App.State) => state.experience.experienceLists,
  (
    bedbankViews,
    tourV2Views,
    hotelViews,
    villaViews,
    flightViews,
    closestAirport,
    isSpoofed,
    recentlyUsedAirportCode,
    regionCode,
    airports,
    experienceLists,
  ): Array<ExperienceListFilters> => {
    const bedbankViewsDataValid = bedbankViews.hasRequiredData
    const tourV2ViewsDataValid = tourV2Views.hasRequiredData
    const hotelViewsDataValid = hotelViews.hasRequiredData
    const villaViewsDataValid = villaViews.hasRequiredData
    const flightViewsDataValid = flightViews.hasRequiredData

    if (!bedbankViewsDataValid || !tourV2ViewsDataValid || !hotelViewsDataValid || !villaViewsDataValid || !flightViewsDataValid) return []

    const filters = buildLoyaltyLoungePassFilters({
      bedbankItems: bedbankViews.data,
      tourItems: tourV2Views.data,
      hotelItems: hotelViews.data,
      villaItemsViews: villaViews.data,
      flightItems: flightViews.data,
      experienceLists,
      airports,
      isSpoofed,
      recentlyUsedAirportCode,
      regionCode,
      closestAirport,
    })

    return filters
  },
)

function isLoungeExperienceItem(item: App.Checkout.AnyItem, experienceItemsView: Array<App.Checkout.ExperienceItemView>): boolean {
  return item.itemType === CHECKOUT_ITEM_TYPE_EXPERIENCE &&
    experienceItemsView.some(itemView => itemView.item.itemId === item.itemId && itemView.categories.some(cat => cat.id === EXP_CATEGORY_AIRPORT_LOUNGE_ID))
}

export const isFlightLoungePassExperienceEnabled = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getCanRedeemLoungePassLoyaltyBenefit(state),
  (state: App.State) => getOptimizelyFeatureFlagVariation(state, OptimizelyFeatureFlags.standaloneFlightLoungePassExperiencesEnabled),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.geo.airports,
  (state: App.State) => state.geo.currentRegionCode.toLowerCase(),
  (state: App.State) => getHotelItemViews(state),
  (state: App.State) => getBedbankItemViews(state),
  (flightItems, experienceViews, canRedeemLoungePassLoyaltyBenefit, standaloneFlightLoungePassExperiencesEnabled, allItems, airports, regionCode, hotelItemViews, bedbankItemViews): boolean => {
    if (!standaloneFlightLoungePassExperiencesEnabled) return false

    // Loyalty lounge pass has priority
    if (canRedeemLoungePassLoyaltyBenefit) return false

    const hasFlights = flightItems.data.length > 0
    const hasAccommodation = hotelItemViews.data.length > 0 || bedbankItemViews.data.length > 0

    const airportsByCode = arrayToObject(airports, airport => airport.code)
    const isInternationalFlightTrip = flightItems.data.some(view =>
      view.flights.some(flight =>
        flight.journeyFlight.flights.some(flight => {
          const departingAirport = airportsByCode[flight.departingAirport]
          const arrivingAirport = airportsByCode[flight.arrivalAirport]
          return (
            departingAirport?.countryCode && departingAirport.countryCode.toLowerCase() !== regionCode ||
            arrivingAirport?.countryCode && arrivingAirport.countryCode.toLowerCase() !== regionCode
          )
        }),
      ),
    )
    // For hotel-only carts (no flights), check if the hotel destination is international
    const isInternationalHotelOnly = !hasFlights && (
      hotelItemViews.data.some(view => view.locationCountryCode && view.locationCountryCode.toLowerCase() !== regionCode) ||
      bedbankItemViews.data.some(view => view.locationCountryCode && view.locationCountryCode.toLowerCase() !== regionCode)
    )
    const isInternationalTrip = isInternationalFlightTrip || isInternationalHotelOnly

    const isEligibleCart = (hasFlights || hasAccommodation) && allItems.every(item =>
      isFlightItem(item) ||
      isAccommodationItem(item) ||
      isInsuranceItem(item) ||
      isBookingProtectionItem(item) ||
      isLuxPlusSubscriptionItem(item) ||
      isLoungeExperienceItem(item, experienceViews.data),
    )

    const hasIncludedLounge = flightItems.data.some(view =>
      view.flights.some(flight => !!flight.fareFamily?.fareFamily.fareFeatures.some(feature => feature.type === 'lounge' && feature.status === 'INCLUDED')),
    )

    return isEligibleCart && isInternationalTrip && !hasIncludedLounge
  },
)

export type LoungePassExperienceFilter = {
  filters: ExperienceListFilters
  fromDate: string
  toDate: string
  // flight-specific fields — undefined for hotel-only carts
  fromTime?: string
  toTime?: string
  flightEventAtStartDate?: 'departure' | 'arrival'
  flightEventAtEndDate?: 'departure' | 'arrival'
  fromAirportCode?: string
  toAirportCode?: string
  fareType?: string
  mainDepartureDate?: string
}

/** Flight-only lounge filters. Prefer getLoungePassExperienceFilters for general use. */
export const getFlightLoungePassExperienceFilters = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => state.geo.airports,
  (flightViews, airports): Array<LoungePassExperienceFilter> => {
    if (!flightViews.data.length || !flightViews.hasRequiredData) return []

    const airportsByCode = arrayToObject(airports, airport => airport.code)
    const flightFilters = flightViews.data.map(view =>
      view.flights
        .map(flight =>
          flight.journeyFlight.flights
            .map(journeyFlight => {
              const isFirstFlightInJourney = journeyFlight === flight.journeyFlight.flights[0]

              return [
                {
                  from: journeyFlight.departingDate,
                  to: journeyFlight.departingDate,
                  latitude: airportsByCode[journeyFlight.departingAirport]?.latitude,
                  longitude: airportsByCode[journeyFlight.departingAirport]?.longitude,
                  departingTime: journeyFlight.departingTime,
                  airportCode: journeyFlight.departingAirport,
                  fareType: view.fareType,
                  isFirstFlightInJourney,
                },
                {
                  from: journeyFlight.arrivalDate,
                  to: journeyFlight.arrivalDate,
                  latitude: airportsByCode[journeyFlight.arrivalAirport]?.latitude,
                  longitude: airportsByCode[journeyFlight.arrivalAirport]?.longitude,
                  arrivingTime: journeyFlight.arrivalTime,
                  airportCode: journeyFlight.arrivalAirport,
                  fareType: view.fareType,
                  isFirstFlightInJourney,
                },
              ]
            })
            .flat(),
        ),
    ).flat(2)

    const validFilters = flightFilters.filter(filter => !!filter.from && !!filter.to && !!filter.latitude && !!filter.longitude)

    // Group filters by location and get the date range for each
    const filtersGroupedByLocation = Array.from(groupBy(validFilters, (filter) => `${filter.latitude},${filter.longitude}`).values()).filter(isNonEmptyArray)
    const flightsFilters = filtersGroupedByLocation.map(filters => {
      const earliestFilter = min(filters, (filter) => new Date(filter.from))
      const latestFilter = max(filters, (filter) => new Date(filter.to))

      const fromTime = earliestFilter.departingTime ?? earliestFilter.arrivingTime
      const toTime = latestFilter.arrivingTime ?? latestFilter.departingTime

      const fromDateTime = `${earliestFilter.from}T00:00:00`
      const toDateTime = `${latestFilter.to}T23:59:59`

      // This is to pre-select the date in the calendar for customer's convenience
      const mainDepartureDate = !earliestFilter.isFirstFlightInJourney && latestFilter.isFirstFlightInJourney ? latestFilter.from : earliestFilter.from

      return {
        filters: {
          from: fromDateTime,
          to: toDateTime,
          latitude: filters[0].latitude,
          longitude: filters[0].longitude,
          distanceRange: ['0KM', '5KM'], // Search radius around airport location for nearby lounges
          categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID],
          showUnlisted: true,
        },
        fromDate: earliestFilter.from,
        toDate: latestFilter.to,
        fromTime,
        toTime,
        flightEventAtStartDate: earliestFilter.departingTime ? 'departure' : 'arrival',
        flightEventAtEndDate: latestFilter.departingTime ? 'departure' : 'arrival',
        fromAirportCode: earliestFilter.airportCode,
        toAirportCode: latestFilter.airportCode,
        fareType: filters[0].fareType,
        mainDepartureDate,
      } as LoungePassExperienceFilter
    })

    return flightsFilters
  },
)

function buildDestinationLoungeFilters(
  views: Array<{ startDate?: string; endDate?: string; offer?: { property?: { latitude: number; longitude: number } } }>,
): Array<LoungePassExperienceFilter> {
  return views
    .filter(view => !!view.startDate && !!view.endDate && view.offer?.property?.latitude != null && view.offer?.property?.longitude != null)
    .map(view => {
      const fromDate = moment(view.startDate).subtract(3, 'days').format(ISO_DATE_FORMAT)
      const toDate = moment(view.endDate).add(3, 'days').format(ISO_DATE_FORMAT)
      const { latitude, longitude } = view.offer!.property!

      return {
        filters: {
          from: `${fromDate}T00:00:00`,
          to: `${toDate}T23:59:59`,
          latitude,
          longitude,
          distanceRange: ['0KM', '50KM'], // Wider radius: hotel coords are not co-located with airports
          categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID],
          showUnlisted: true,
        },
        fromDate,
        toDate,
      }
    })
}

export const getHotelOnlyLoungePassFilters = createSelector(
  (state: App.State) => getHotelItemViews(state),
  (state: App.State) => getBedbankItemViews(state),
  (state: App.State) => state.auth.account.recentlyUsedAirportCode,
  (state: App.State) => state.geo.airports,
  (state: App.State) => state.geo.latitude,
  (state: App.State) => state.geo.longitude,
  (state: App.State) => state.geo.mainAirport,
  (leHotelItemViews, bedbankItemViews, recentlyUsedAirportCode, airports, latitude, longitude, mainAirport): Array<LoungePassExperienceFilter> => {
    // Destination filters: search near the hotel property (wide radius, catches nearby airports)
    const destinationFilters = [
      ...buildDestinationLoungeFilters(leHotelItemViews.data),
      ...buildDestinationLoungeFilters(bedbankItemViews.data),
    ]

    // Origin filter: find the user's closest real airport (skip placeholder (0,0) entries)
    const airportsByCode = arrayToObject(airports, airport => airport.code)

    // Aggregate codes (e.g. MELALL, SYDALL) carry (0,0) coords — resolve them to their
    // base airport code (e.g. MELALL → MEL) which has real coordinates in the full list.
    const resolveAirport = (airport: App.Airport | undefined): App.Airport | undefined => {
      if (!airport) return undefined
      if (airport.latitude !== 0 || airport.longitude !== 0) return airport
      if (airport.code.endsWith('ALL')) {
        const base = airportsByCode[airport.code.slice(0, -3)]
        if (base && (base.latitude !== 0 || base.longitude !== 0)) return base
      }
      return undefined
    }

    const realAirports = airports.filter(a => a.latitude !== 0 || a.longitude !== 0)
    const gpsAirport = findClosestAirport(realAirports, latitude, longitude)

    const referenceAirport =
      gpsAirport ??
      resolveAirport(mainAirport) ??
      resolveAirport(recentlyUsedAirportCode ? airportsByCode[recentlyUsedAirportCode] : undefined)

    if (!referenceAirport) return destinationFilters

    const allViews = [
      ...leHotelItemViews.data,
      ...bedbankItemViews.data,
    ].filter(view => !!view.startDate && !!view.endDate)

    if (!allViews.length) return destinationFilters

    const earliestView = min(allViews, view => new Date(view.startDate!))
    const latestView = max(allViews, view => new Date(view.endDate!))
    if (!earliestView || !latestView) return destinationFilters

    const fromDate = moment(earliestView.startDate).subtract(3, 'days').format(ISO_DATE_FORMAT)
    const toDate = moment(latestView.endDate).add(3, 'days').format(ISO_DATE_FORMAT)

    const originFilter: LoungePassExperienceFilter = {
      filters: {
        from: `${fromDate}T00:00:00`,
        to: `${toDate}T23:59:59`,
        latitude: referenceAirport.latitude,
        longitude: referenceAirport.longitude,
        distanceRange: ['0KM', '5KM'],
        categoryCodes: [EXP_CATEGORY_AIRPORT_LOUNGE_ID],
        showUnlisted: true,
      },
      fromDate,
      toDate,
    }

    return [...destinationFilters, originFilter]
  },
)

export const getLoungePassExperienceFilters = createSelector(
  (state: App.State) => getFlightLoungePassExperienceFilters(state),
  (state: App.State) => getHotelOnlyLoungePassFilters(state),
  (flightFilters, hotelFilters): Array<LoungePassExperienceFilter> => {
    return flightFilters.length > 0 ? flightFilters : hotelFilters
  },
)

export const getFlightLoungePassExperiencesWithFilters = createSelector(
  (state: App.State) => getLoungePassExperienceFilters(state),
  (state: App.State) => state.experience.experiences,
  (state: App.State) => state.experience.experienceLists,
  (state: App.State) => getIsInternationalFlight(state),
  (state: App.State) => isSpoofed(state),
  (flightLoungePassFilters, allExperiences, experienceLists, isInternational, isSpoofed) => {
    // Hotel-only filters have no fromAirportCode. When no flight-based filters are present,
    // pass undefined so collinsonLoungeSupportsFlightType allows all lounge types.
    const hasFlightFilters = flightLoungePassFilters.some(f => f.fromAirportCode !== undefined)
    const effectiveIsInternational = hasFlightFilters ? isInternational : undefined
    const allResults = flightLoungePassFilters.map(flightsFilters => {
      const listKey = getExperienceListKey(flightsFilters.filters)
      const experiences = experienceLists?.[listKey]?.ids.map(id => allExperiences[id]).filter(Boolean) ?? []

      const mapped = experiences
        .filter(experience => collinsonLoungeSupportsFlightType(experience, effectiveIsInternational))
        .map(experience => ({
          experience,
          filters: flightsFilters.filters,
          fetching: !!experienceLists?.[listKey]?.fetching,
          fromDate: flightsFilters.fromDate,
          toDate: flightsFilters.toDate,
          fromTime: flightsFilters.fromTime,
          toTime: flightsFilters.toTime,
          flightEventAtStartDate: flightsFilters.flightEventAtStartDate,
          flightEventAtEndDate: flightsFilters.flightEventAtEndDate,
          fromAirportCode: flightsFilters.fromAirportCode,
          toAirportCode: flightsFilters.toAirportCode,
          fareType: flightsFilters.fareType,
          mainDepartureDate: flightsFilters.mainDepartureDate,
        }))

      return isSpoofed ? mapped : filterCollinsonLounges(mapped, l => l.experience.provider)
    }).flat()

    return allResults
  },
)

/**
 * Experience booking views for the lounge pass booking modal.
 * Unlike getAllPurchasableExperiences, this includes Collinson (provider='cln') offers
 * which are unlisted and filtered out of the general checkout inflow.
 */
export const getLoungePassBookingExperiences = createSelector(
  (state: App.State) => getExperiencesViews(state),
  (state: App.State) => getFlightLoungePassExperiencesWithFilters(state),
  (experienceViews, loungePassResults): Array<App.ExperienceBookingView> => {
    const loungeIds = new Set(loungePassResults.map(l => l.experience.id))
    return experienceViews.filter(view => loungeIds.has(view.id))
  },
)

export const getFlightLoungePassExperienceAvailable = createSelector(
  (state: App.State) => isFlightLoungePassExperienceEnabled(state),
  (state: App.State) => getFlightLoungePassExperiencesWithFilters(state),
  (isFlightLoungePassExperienceEnabled, loungeExperiences) => {
    return isFlightLoungePassExperienceEnabled && (loungeExperiences.length > 0 || loungeExperiences.some(lounge => lounge.fetching))
  },
)

export const shouldPrefetchExperiencesInFlow = createSelector(
  (state: App.State) => getExperiencesEnabled(state),
  (_state: App.State, offer: App.HotelOffer | App.VillaOffer | undefined) => offer,
  (_state: App.State, _offer: App.HotelOffer | App.VillaOffer | undefined, disablePrefetchExperiences?: boolean) => !!disablePrefetchExperiences,
  (experiencesEnabled, offer, disablePrefetchExperiences): boolean => {
    if (disablePrefetchExperiences) return false
    const willShowExperiencesInFlow = !!offer && offer.type !== 'rental' && !!offer.experiencesInFlow
    return experiencesEnabled && willShowExperiencesInFlow
  },
)

export const getAccommodationTagIds = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getAccommodationStartDate(state),
  (state: App.State) => getAccommodationEndDate(state),
  (state: App.State) => state.offer.offerTags,
  (accommodationItems, startDate, endDate, offerTags) => {
    const accommodationItem = accommodationItems[0]
    if (!accommodationItem?.offerId) return []
    const key = buildOfferTagsCacheKey(accommodationItem.offerId, startDate, endDate)
    return offerTags[key]?.tags.map(tag => tag.id) ?? []
  },
)

const NormalizedBookingTypes: Record<ExperienceBookingType, Experiences.ValidateExperienceBookingItem['bookingType']> = {
  [ExperienceBookingType.BUY_NOW_BOOK_LATER]: 'BUY-NOW-BOOK-LATER',
  [ExperienceBookingType.INSTANT_BOOKING]: 'INSTANT-BOOKING',
}

export const getValidateCheckoutExperiencesItemsPayload = createSelector(
  (state: App.State) => getFormattedExperienceItems(state),
  (state: App.State) => getFormattedTransferItems(state),
  (state: App.State) => getFormattedLoyaltyLoungePassItems(state),
  (state: App.State) => getHasLuxPlusOrSubscriptionInCart(state),
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => getLoyaltyLoungePassItemViews(state),
  (state: App.State) => getTransferItemsView(state),
  (
    formattedExperienceItems,
    formattedTransferItems,
    formattedLoyaltyLoungePassItems,
    hasLuxPlusOrSubscriptionInCart,
    currencyCode,
    experienceItemsViews,
    loyaltyLoungeItemsViews,
    transferItemsViews,
  ) => {
    if (!experienceItemsViews.hasRequiredData || !loyaltyLoungeItemsViews.hasRequiredData || !transferItemsViews.hasRequiredData) return
    if (!!experienceItemsViews.data.length && !formattedExperienceItems.length) return
    if (!!loyaltyLoungeItemsViews.data.length && !formattedLoyaltyLoungePassItems.length) return
    if (!!transferItemsViews.data.length && !formattedTransferItems.length) return

    const allFormattedItems = [
      ...formattedExperienceItems,
      ...formattedTransferItems,
      ...formattedLoyaltyLoungePassItems,
    ].filter((item) => !!item)

    const allTicketItemsViews = [
      ...experienceItemsViews.data.flatMap((itemView) => itemView.ticketViews.map((ticketView) => ticketView)),
      ...loyaltyLoungeItemsViews.data,
      ...transferItemsViews.data,
    ]

    const ticketsViewsByTransactionKey = arrayToObject(allTicketItemsViews, (itemView) => itemView.item.transactionKey)

    const payload: Experiences.ValidateExperienceItemsPayload = {
      currency: currencyCode,
      brand: config.BRAND as Experiences.ValidateExperienceItemsPayload['brand'],
      isMemberSubscriber: hasLuxPlusOrSubscriptionInCart,
      bookingItems: allFormattedItems.map((item) => {
        const isTransfer = 'deal_options' in item
        const orderItemId = 'id_experience_items' in item ? item.id_experience_items : undefined

        const ticketView = ticketsViewsByTransactionKey[item.transaction_key]

        return {
          experienceItemId: orderItemId ?? ticketView.item.itemId,
          // Using transaction_key to keep track of every change (date, time...)
          // even if the item is still the same in cart
          transactionKey: item.transaction_key,
          experienceOfferId: item.provider_offer_id,
          productId: item.ticket.productId,
          salePrice: item.total,
          fareType: item.ticket.fareType,
          language: !isTransfer ? item.language : undefined,
          pickupId: item.pickup_point_id,
          travellerInfo: {
            customer: item.traveller_info?.customer,
            participants: item.traveller_info && 'participants' in item.traveller_info ? item.traveller_info.participants : undefined,
          },
          bookingDate: item.ticket.date,
          specialRequests: item.special_requests,
          redemptionLocationId: !isTransfer ? item.redemption_location_id : undefined,
          pickupName: item.pickup_point_name,
          dropoffId: isTransfer ? item.dropoff_point_id : undefined,
          dropoffName: isTransfer ? item.dropoff_point_name : undefined,
          bookingType: !isTransfer ? NormalizedBookingTypes[item.booking_type] : NormalizedBookingTypes[ExperienceBookingType.INSTANT_BOOKING],
          bookByDate: !isTransfer ? item.ticket.bookByDate : item.book_by_date,
          rateStartDate: !isTransfer ? item.ticket.rateStartDate : undefined,
          rateEndDate: !isTransfer ? item.ticket.rateEndDate : undefined,
          appDiscountAmount: item.app_discount_amount,
          sourceApp: item.source_app,
          taxesAndFees: item.taxes_and_fees,
          ...(isTransfer && item.deal_options && {
            dealOptions: {
              adults: item.deal_options.adults,
              children: item.deal_options.children,
              child_seats: item.deal_options.child_seats,
              booster_seats: item.deal_options.booster_seats,
              flight_number: item.deal_options.flight_number,
              bags: item.deal_options.bags,
              oversized_bags: item.deal_options.oversized_bags,
              type: item.deal_options.type,
              option: {
                id: item.deal_options.option.id,
                name: item.deal_options.option.name,
                price: item.deal_options.option.price,
                experienceId: item.deal_options.option.experienceId,
                hasChildSeats: item.deal_options.option.hasChildSeats,
                hasBoosterSeats: item.deal_options.option.hasBoosterSeats,
                maxPassengers: item.deal_options.option.maxPassengers,
                transferTypes: item.deal_options.option.transferTypes,
                maxCarryOnBags: item.deal_options.option.maxCarryOnBags,
                maxCheckedBags: item.deal_options.option.maxCheckedBags,
                maxTransferDistance: item.deal_options.option.maxTransferDistance,
              },
            },
          }),
        }
      }),
    }

    return payload
  },
)
